import { FC, ReactElement } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { MessageDescriptor } from 'react-intl';
import { useMedia } from 'react-use';

import { BUTTON_TYPES, media } from '@savgroup-front-common/constants';
import dataTestId from '@savgroup-front-common/constants/dataTestId';
import { MessageType } from '@savgroup-front-common/types';

import { Button } from '../../../atoms/button';
import { Input } from '../../../atoms/Form';
import { QrCodeImportModal } from '../../../atoms/QrCodeImportModal/QrCodeImportModal';
import ScanButton from '../../../atoms/ScanButton/ScanButton';
import { QrCodeIcon } from '../../../protons/icons/QrCode.icon';
import { useAdditionalInformationContext } from '../AdditionalInformation.context';
import { AdditionalInformation } from '../hooks/useAdditionalInformation';

import { useAdditionalInformationSerialNumber } from './AdditionalInformationSerialNumber.hooks';

interface AdditionalInformationSerialNumberProps {
  neededInformationItem: AdditionalInformation;
  label?: MessageType | string;
  name: string;
  postLabel?: string | ReactElement;
  placeholder?: string | MessageType | MessageDescriptor;
}

export const AdditionalInformationSerialNumber: FC<
  AdditionalInformationSerialNumberProps
> = ({ label, neededInformationItem, name, postLabel, placeholder }) => {
  const isMobileView = useMedia(media.maxWidth.xs);
  const { id, isRequired, claimIds } = neededInformationItem;
  const { onAdditionalInformationSave, sellerId } =
    useAdditionalInformationContext();

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const {
    isOpen,
    onNewFileUpload,
    isLoading,
    handleUploadFinishClick,
    handleQrCodeButtonClick,
  } = useAdditionalInformationSerialNumber({
    name,
    neededInformationItem,
  });

  return (
    <>
      {!isMobileView && claimIds[0] && (
        <QrCodeImportModal
          id={id}
          sellerId={sellerId}
          isOpen={isOpen}
          onUploadFinishClick={handleUploadFinishClick}
          isSubmitLoading={isLoading}
        />
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <Input
            name={field.name}
            errors={errors}
            isRequired={isRequired}
            label={label}
            onChange={field.onChange}
            internalId={field.name}
            value={field.value}
            isDisabled={isLoading}
            onBlur={async () => {
              if (onAdditionalInformationSave) {
                await onAdditionalInformationSave();
              }
            }}
            dataTestId={`additional-information-input-input-${id}`}
            hasIconOnError={false}
            postLabel={postLabel}
            placeholder={placeholder}
          >
            <ScanButton
              onNewFileUpload={onNewFileUpload}
              isLoading={isLoading}
            />
            {!isMobileView && (
              <Button
                disabled={isLoading}
                primary
                hollow
                onClick={handleQrCodeButtonClick}
                type={BUTTON_TYPES.BUTTON}
                icon={<QrCodeIcon />}
                isLoading={isLoading}
                dataTestId={`qrCodeButton_${dataTestId}`}
              />
            )}
          </Input>
        )}
      />
    </>
  );
};
